/**
 * The session timer (unit 26B; v1's `recording` store and `RecordingBar`).
 *
 * Only the start is kept — the time is always counted from it — so the timer
 * is right after a reload or a phone's sleep. It is kept per browser and per
 * person (F4). **The recording ends only when its save lands**: a stop with no
 * signal keeps the bar up, to try again.
 *
 * **Piano lands in `piano_sessions`** (v1.99), where the Piano widget, the coach
 * and every piece's history read it; anything else is an activity. One row,
 * never two: Turner already draws piano sessions as a source of its own.
 */
import { create } from "zustand";

import { api } from "../api/client";
import { ok } from "../api/query";
import type { Recordable } from "../chat/commands";
import { usePrefs } from "../prefs/store";

export interface Recording extends Recordable {
  startMs: number;
  /** `YYYY-MM-DDTHH:MM:SS`, local: the form a hand-logged activity takes. */
  startTime: string;
}

const key = () => `kindred:${usePrefs.getState().account ?? "signed-out"}:recording`;

function load(): Recording | null {
  try {
    const stored = localStorage.getItem(key());
    return stored ? (JSON.parse(stored) as Recording) : null;
  } catch {
    return null;
  }
}

function keep(recording: Recording | null): void {
  try {
    if (recording) localStorage.setItem(key(), JSON.stringify(recording));
    else localStorage.removeItem(key());
  } catch {
    // Without it the bar lasts until the page closes.
  }
}

function localIso(at: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}:${pad(at.getSeconds())}`;
}

const TAG = "kindred-recording";

async function notify(label: string | null): Promise<void> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    if (label === null) {
      for (const shown of await registration.getNotifications({ tag: TAG })) shown.close();
    } else if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      await registration.showNotification(`Recording: ${label}`, {
        tag: TAG,
        body: "Tap to return to Kindred",
        icon: "/icons/icon-192.png",
        silent: true,
        data: { url: "/" },
      });
    }
  } catch {
    // A notification is a courtesy; the bar is the record.
  }
}

/** The row the recording becomes. */
export async function save(recording: Recording, now = Date.now()): Promise<void> {
  const duration_secs = Math.max(1, Math.floor((now - recording.startMs) / 1000));
  if (recording.type === "piano") {
    await ok(
      api.POST("/api/piano/practice", {
        body: {
          name: recording.label,
          duration_secs,
          started_at: recording.startTime,
          source: "manual",
          notes: "",
          metrics: {},
          pieces: [],
          takes: [],
        },
      }),
    );
    return;
  }
  await ok(
    api.POST("/api/health/activities", {
      body: {
        activity_type: recording.type,
        start_time: recording.startTime,
        duration_secs,
        name: recording.label,
      },
    }),
  );
}

interface Recorder {
  recording: Recording | null;
  saving: boolean;
  failed: boolean;
  /** Read this person's recording, after sign-in. */
  bind(): void;
  start(what: Recordable): void;
  stop(): Promise<boolean>;
  cancel(): void;
}

export const useRecorder = create<Recorder>()((set, get) => ({
  recording: null,
  saving: false,
  failed: false,
  bind: () => set({ recording: load(), saving: false, failed: false }),
  start(what) {
    const now = new Date();
    const recording: Recording = { ...what, startMs: now.getTime(), startTime: localIso(now) };
    keep(recording);
    set({ recording, failed: false });
    void notify(what.label);
  },
  async stop() {
    const recording = get().recording;
    if (!recording || get().saving) return false;
    set({ saving: true, failed: false });
    try {
      await save(recording);
    } catch {
      set({ saving: false, failed: true });
      return false;
    }
    keep(null);
    set({ recording: null, saving: false });
    void notify(null);
    return true;
  },
  cancel() {
    keep(null);
    set({ recording: null, failed: false });
    void notify(null);
  },
}));
